import { useState } from "react";

export interface DonutChartItem {
  name: string;
  count: number;
  color: string;
}

export interface DonutChartProps {
  title: string;
  subtitle?: string;
  items: DonutChartItem[];
  isLoading?: boolean;
}

function donutSlicePath(
  cx: number,
  cy: number,
  outerR: number,
  innerR: number,
  startDeg: number,
  endDeg: number,
): string {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const s = toRad(startDeg);
  const e = toRad(endDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  const o1 = [cx + outerR * Math.cos(s), cy + outerR * Math.sin(s)];
  const o2 = [cx + outerR * Math.cos(e), cy + outerR * Math.sin(e)];
  const i1 = [cx + innerR * Math.cos(s), cy + innerR * Math.sin(s)];
  const i2 = [cx + innerR * Math.cos(e), cy + innerR * Math.sin(e)];
  return [
    `M ${o1[0]} ${o1[1]}`,
    `A ${outerR} ${outerR} 0 ${largeArc} 1 ${o2[0]} ${o2[1]}`,
    `L ${i2[0]} ${i2[1]}`,
    `A ${innerR} ${innerR} 0 ${largeArc} 0 ${i1[0]} ${i1[1]}`,
    "Z",
  ].join(" ");
}

export function DonutChart({
  title,
  subtitle,
  items,
  isLoading,
}: DonutChartProps) {
  const total = items.reduce((s, a) => s + a.count, 0);
  const cx = 90,
    cy = 90,
    outerR = 78,
    innerR = 46;
  const [hovered, setHovered] = useState<DonutChartItem | null>(null);

  if (isLoading && items.length === 0) {
    return (
      <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
        <div className="border-(--color-border) border-b px-6 py-4">
          <p className="text-(--color-accent) text-sm font-bold">{title}</p>
          {subtitle !== undefined && (
            <p className="text-(--color-text-secondary) mt-0.5 text-xs">
              Carregando...
            </p>
          )}
        </div>
        <div className="flex items-center justify-center px-4 py-10">
          <div className="h-40 w-40 animate-pulse rounded-full bg-white/10" />
        </div>
      </div>
    );
  }

  let angle = -90;
  const slices = items.map((area) => {
    const sweep = total > 0 ? (area.count / total) * 360 : 0;
    const path = donutSlicePath(cx, cy, outerR, innerR, angle, angle + sweep);
    angle += sweep;
    return { ...area, path };
  });

  return (
    <div className="border-(--color-accent)/40 bg-(--color-bg-primary)/60 overflow-hidden rounded-2xl border backdrop-blur-sm">
      <div className="border-(--color-border) border-b px-6 py-4">
        <p className="text-(--color-accent) text-sm font-bold">{title}</p>
        {subtitle !== undefined && (
          <p className="text-(--color-text-secondary) mt-0.5 text-xs">
            {subtitle}
          </p>
        )}
      </div>

      <div className="flex flex-col items-center gap-6 px-4 py-5 sm:flex-row sm:gap-8 sm:px-6 sm:py-6">
        <svg
          viewBox="0 0 180 180"
          className="h-40 w-40 shrink-0 sm:h-44 sm:w-44"
        >
          {slices.map((s) => (
            <path
              key={s.name}
              d={s.path}
              fill={s.color}
              opacity={hovered ? (hovered.name === s.name ? 1 : 0.35) : 0.9}
              className="cursor-pointer transition-opacity"
              onMouseEnter={() => setHovered(s)}
              onMouseLeave={() => setHovered(null)}
            />
          ))}
          {hovered ? (
            <>
              <text
                x={cx}
                y={cy - 8}
                textAnchor="middle"
                fontSize={20}
                fontWeight="bold"
                fill={hovered.color}
              >
                {total > 0 ? Math.round((hovered.count / total) * 100) : 0}%
              </text>
              <text
                x={cx}
                y={cy + 10}
                textAnchor="middle"
                fontSize={8}
                fill="var(--color-text-secondary)"
              >
                {hovered.count}
              </text>
            </>
          ) : (
            <>
              <text
                x={cx}
                y={cy - 7}
                textAnchor="middle"
                fontSize={18}
                fontWeight="bold"
                fill="white"
              >
                {total}
              </text>
              <text
                x={cx}
                y={cy + 10}
                textAnchor="middle"
                fontSize={9}
                fill="var(--color-text-secondary)"
              >
                total
              </text>
            </>
          )}
        </svg>

        <ul className="flex-1 space-y-3">
          {items.map((area) => (
            <li
              key={area.name}
              className="flex cursor-pointer items-center justify-between gap-3 transition-opacity"
              style={{
                opacity: hovered && hovered.name !== area.name ? 0.4 : 1,
              }}
              onMouseEnter={() => setHovered(area)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: area.color }}
                />
                <span className="text-(--color-text-primary) truncate text-sm">
                  {area.name}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="text-(--color-text-secondary) text-xs">
                  {area.count}
                </span>
                <span
                  className="w-9 text-right text-xs font-bold tabular-nums"
                  style={{ color: area.color }}
                >
                  {total > 0 ? Math.round((area.count / total) * 100) : 0}%
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
